import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface CustomerContact {
  id: string;
  customer_id: string;
  company_id: string;
  name: string;
  title: string | null;
  email: string | null;
  phone: string | null;
  is_primary: boolean;
  receives_estimates: boolean;
  receives_invoices: boolean;
  notes: string | null;
  created_at: string;
}

export const useCustomerContacts = (customerId: string | undefined) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["customer_contacts", customerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customer_contacts")
        .select("*")
        .eq("customer_id", customerId!)
        .order("is_primary", { ascending: false });
      if (error) throw error;
      return data as CustomerContact[];
    },
    enabled: !!user && !!customerId,
  });
};

export interface CreateContactInput {
  customer_id: string;
  name: string;
  title?: string;
  email?: string;
  phone?: string;
  is_primary?: boolean;
  receives_estimates?: boolean;
  receives_invoices?: boolean;
  notes?: string;
}

export const useCreateCustomerContact = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateContactInput) => {
      const { data: companyId, error: cidErr } = await supabase.rpc("my_company_id");
      if (cidErr) throw cidErr;
      const { error } = await supabase.from("customer_contacts").insert({
        ...input,
        company_id: companyId,
      });
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["customer_contacts", vars.customer_id] });
    },
  });
};

export const useUpdateCustomerContact = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, customer_id, ...input }: CreateContactInput & { id: string }) => {
      const { error } = await supabase
        .from("customer_contacts")
        .update(input)
        .eq("id", id);
      if (error) throw error;
      return customer_id;
    },
    onSuccess: (customerId) => {
      qc.invalidateQueries({ queryKey: ["customer_contacts", customerId] });
    },
  });
};

export const useDeleteCustomerContact = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, customer_id }: { id: string; customer_id: string }) => {
      const { error } = await supabase.from("customer_contacts").delete().eq("id", id);
      if (error) throw error;
      return customer_id;
    },
    onSuccess: (customerId) => {
      qc.invalidateQueries({ queryKey: ["customer_contacts", customerId] });
    },
  });
};
